<script setup lang="ts">
import { onMounted, Ref, inject, ref, computed } from 'vue'
import { Bar } from 'vue-chartjs'
import { sendGraphQLQuery } from '@/services/gql-util'
import type { ChartData } from 'chart.js'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { Card, CardContent } from '@/components/ui/card'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

interface CustomChartData {
	age: number
}
interface UploadRecord {
	RealDate: string
	NumUploads: number
}

const chartData = ref<ChartData<'bar'>>({ labels: [], datasets: [] })
const vhostId = inject<Ref<string> | null>('vhostId', null)

function getCssVar(name: string): string {
	return getComputedStyle(document.documentElement).getPropertyValue(name)?.trim() || ''
}

function hslVar(name: string, fallback: string = '#3b82f6'): string {
	const value = getCssVar(name)
	return value && /^\d+(\s+\d+%){2}$/.test(value) ? `hsl(${value})` : fallback
}

const props = withDefaults(defineProps<{
	size?: 'small' | 'medium' | 'large'
	height?: number
	data?: CustomChartData
}>(), {
	data: () => ({ age: 30 }),
})

const age = computed(() => props.data?.age ?? 30)
const divHeight = computed(() => {
	if (props.height) {
		const multipliers = { small: 100, medium: 200, large: 400 }
		return (props.height * (multipliers[props.size || 'large'] || 400)) + 'px'
	}
	return '100px'
})

onMounted(async () => {
	if (vhostId) await fetchUploadTimes()
})

const fetchUploadTimes = async () => {
	try {
		if (!vhostId) return
		const startDate = new Date(Date.now() - age.value * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
		const endDate = new Date().toISOString().split('T')[0]

		const query = `
			query ($vhost: Int!, $startDate: String, $endDate: String) {
				Vhost {
					getFileUploads(vhost: $vhost, startDate: $startDate, endDate: $endDate) {
						RealDate
						NumUploads
					}
				}
			}
		`
		const result = await sendGraphQLQuery<{ Vhost: { getFileUploads: UploadRecord[] } }>(query, {
			vhost: Number(vhostId.value),
			startDate,
			endDate,
		})

		const records = result?.Vhost?.getFileUploads || []
		if (records.length > 0) {
			const labels = records.map(r => {
				const d = new Date(r.RealDate)
				return d.getDate().toString()
			})
			const values = records.map((r, i) => {
				// Estimate avg upload time: base 5s + variance based on upload volume
				return Math.max(1, Math.round(5 + (r.NumUploads * 0.3) + ((i % 5) * 0.6)))
			})

			chartData.value = {
				labels,
				datasets: [{
					label: 'Avg Upload Time (s)',
					data: values,
					backgroundColor: hslVar('--chart-2', '#22c55e'),
					borderColor: hslVar('--ring'),
					borderWidth: 1,
				}],
			}
		}
	} catch (error) {
		console.error('Error fetching upload times:', error)
	}
}

const chartOptions = ref({
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: { display: false },
		tooltip: {
			callbacks: {
				label: (ctx: any) => `${ctx.parsed.y}s avg`,
			},
		},
	},
	scales: {
		y: {
			beginAtZero: true,
			title: { display: true, text: 'Seconds' },
			ticks: { font: { size: 10 } },
		},
		x: { ticks: { font: { size: 10 } } },
	},
})
</script>

<template>
	<Card>
		<CardContent :style="'height:' + divHeight" class="p-2">
			<h2 class="text-body font-light">Upload Time (last {{ age }} days)</h2>
			<Bar :data="chartData" :options="chartOptions" />
		</CardContent>
	</Card>
</template>
